import VersusPage, {
  type VersusComparisonRow,
  type VersusFaq,
  type VersusStat,
  type VersusWorkflow,
} from "@/components/public/versus-page"
import type { SeoMeta } from "@/types"

const STATS: VersusStat[] = [
  {
    value: "0",
    label: "cameras needed",
    detail: "No Pro3, no Pro2, no smartphone capture. QuiteRight builds from the photos you already have.",
  },
  {
    value: "60s",
    label: "from URL to ad",
    detail: "Paste a Rightmove, Zoopla or OnTheMarket URL \u2014 finished ad ready to publish.",
  },
  {
    value: "4",
    label: "socials, one click",
    detail: "Publish straight to Instagram, Facebook, TikTok and YouTube from the dashboard.",
  },
  {
    value: "\u221E",
    label: "videos per month",
    detail: "Unlimited rendering on the \u00A3499/month plan \u2014 every listing, every update.",
  },
]

const WORKFLOW: VersusWorkflow = {
  competitorSteps: [
    "Book a site visit with the Matterport camera",
    "Walk and scan the property room by room",
    "Upload the scan to Matterport cloud",
    "Wait for the dollhouse to process",
    "Embed the tour on your listing page",
    "Still need a social video? Not Matterport's job",
  ],
  competitorTotal: "Half-day visit + processing",
  quiterightSteps: [
    "Paste the listing URL",
    "Pick a template and voice",
    "Publish straight to socials",
  ],
  quiterightTotal: "60 seconds",
}

const COMPARISON: VersusComparisonRow[] = [
  { label: "Primary output", competitor: "3D digital twin / dollhouse tour", quiteright: "Short-form social video ad", highlight: true },
  { label: "Where buyers see it", competitor: "Listing page, by click-through", quiteright: "Reels, TikTok and Shorts feeds", highlight: true },
  { label: "Site visit", competitor: "Required per listing", quiteright: "None \u2014 builds from portal photos", highlight: true },
  { label: "Time to first asset", competitor: "Half-day + processing", quiteright: "Under 60 seconds" },
  { label: "Script and voiceover", competitor: "Not included", quiteright: "AI voiceover + property script" },
  { label: "Branded throughout", competitor: "Tour viewer chrome", quiteright: "Agency logo and colours on every frame" },
  { label: "Listings covered", competitor: "Flagship only", quiteright: "Every listing, every week", highlight: true },
  { label: "Pricing model", competitor: "Camera + per-space monthly hosting", quiteright: "Flat \u00A3499/month, unlimited video" },
  { label: "Best use case", competitor: "High-value properties where tour drives viewings", quiteright: "Every listing on every social channel" },
]

const FAQ: VersusFaq[] = [
  {
    q: "Is QuiteRight a Matterport replacement?",
    a: "No. Matterport produces immersive 3D digital twins that buyers walk through on your listing page. QuiteRight produces scroll-stopping short-form video ads that live on social. They solve different problems, and the best agents use both.",
  },
  {
    q: "When does Matterport make sense?",
    a: "Matterport shines on flagship instructions \u2014 high-value sales, new-build show homes, holiday lets where buyers book without visiting. The 3D tour reduces viewings of the wrong type and builds trust for serious buyers.",
  },
  {
    q: "When does QuiteRight make more sense?",
    a: "When you want every listing (not just the flagships) to have a video ad on Instagram, TikTok and Facebook. QuiteRight doesn't need a camera, a tripod, or a site visit \u2014 so it scales across your entire instruction book.",
  },
  {
    q: "Can QuiteRight reuse my Matterport still frames?",
    a: "Yes. Matterport's 2D stills are high-quality. Upload them to the listing in QuiteRight (or list them on a portal first) and QuiteRight will weave them into the video with Ken Burns motion, captions, voiceover and branding.",
  },
  {
    q: "What about 3D floorplans?",
    a: "QuiteRight doesn't produce 3D floorplans \u2014 that's Matterport territory. For 2D floorplans, most agents already get them with their photography package or from tools like BoxBrownie.",
  },
]

export default function VsMatterport({ meta }: { meta: SeoMeta }) {
  return (
    <VersusPage
      meta={meta}
      competitorName="Matterport"
      breadcrumbName="QuiteRight vs Matterport"
      heroHeadline="3D tours vs short-form ads. Different buyer moments"
      heroSubhead="Matterport shows a serious buyer around a flagship listing. QuiteRight stops a scroll on Instagram so the flagship (and every other listing) gets seen in the first place."
      statsHeadline="One camera-free workflow, every listing"
      statsSubhead="Matterport needs a site visit per property. QuiteRight works from portal photos, so every listing gets video marketing \u2014 not just the top 10%."
      stats={STATS}
      workflowHeadline="Tour the flagship. Ad every listing"
      workflow={WORKFLOW}
      comparison={COMPARISON}
      faq={FAQ}
      ctaHeadline="Immersive tours for the flagships, short-form ads for the rest"
      ctaBody="Matterport shows buyers around. QuiteRight gets buyers in the door in the first place."
      relatedKey="vs/matterport"
    />
  )
}
